import {CreateLedgerEntry, LedgerFilterOptions, LedgerSummary} from "@/models/ledger";
import {assertTokenValidForMutation} from "@/services/service-helpers";
import log from '@/lib/logger';

const LEDGER_API = `${import.meta.env.VITE_NBH_URL_ROOT}/api/ledger`;

export async function getEarliestLedgerYear(token: string): Promise<number | null> {
    try {
        const response = await fetch(`${LEDGER_API}/earliest-year`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            log.error('Failed to fetch earliest year:', response.status);
            return null;
        }

        const year = await response.json();
        return year || null;
    } catch (error) {
        log.error('Error fetching earliest ledger year:', error);
        return null;
    }
}

export async function getLedgerEntries(
    token: string,
    options: LedgerFilterOptions = {}
): Promise<LedgerSummary | null> {
    try {
        const params = new URLSearchParams();
        
        if (options.year) params.append('year', options.year.toString());
        if (options.month) params.append('month', options.month.toString());
        if (options.type) params.append('type', options.type);
        // Search is now handled client-side only - don't send to backend
        if (options.page !== undefined) params.append('page', options.page.toString());
        if (options.size !== undefined) params.append('size', options.size.toString());
        if (options.sort) params.append('sort', options.sort);
        if (options.includeCancelled !== undefined) params.append('includeCancelled', options.includeCancelled.toString());
        
        const response = await fetch(`${LEDGER_API}/entries?${params.toString()}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            log.error('Failed to fetch ledger entries:', response.status);
            return null;
        }

        return await response.json();
    } catch (error) {
        log.error('Error fetching ledger entries:', error);
        return null;
    }
}

export async function createLedgerEntry(
    token: string,
    entry: CreateLedgerEntry
): Promise<any> {
    assertTokenValidForMutation()
    const response = await fetch(`${LEDGER_API}/entries`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(entry)
    });

    if (!response.ok) {
        throw await response.json();
    }

    return await response.json();
}

export async function getLedgerEntry(
    token: string,
    entryId: string
): Promise<any> {
    const response = await fetch(`${LEDGER_API}/entries/${entryId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw await response.json();
    }

    return await response.json();
}

export async function updateLedgerEntry(
    token: string,
    entryId: string,
    entry: CreateLedgerEntry
): Promise<any> {
    assertTokenValidForMutation()
    const response = await fetch(`${LEDGER_API}/entries/${entryId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(entry)
    });

    if (!response.ok) {
        throw await response.json();
    }

    return await response.json();
}

export async function cancelLedgerEntry(
    token: string,
    entryId: string
): Promise<void> {
    assertTokenValidForMutation()
    const response = await fetch(`${LEDGER_API}/entries/${entryId}/cancel`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw await response.json();
    }
}

export async function downloadLedgerReceipt(token: string, entryId: string): Promise<Blob> {
    const response = await fetch(`${LEDGER_API}/entries/${entryId}/receipt`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });

    if (!response.ok) {
        throw new Error(`Receipt download failed: ${response.status}`);
    }

    return await response.blob();
}

export async function getOpeningBalance(token: string): Promise<{ amount: number; transactionDate: string } | null> {
    const response = await fetch(`${LEDGER_API}/opening-balance`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    });

    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw await response.json();
    }

    return await response.json();
}

